import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";
import { principalAxes } from "./linalg";

export interface BiteFrame {
  /** Maps bite-frame coordinates back to the scanner / bite-record frame. */
  worldFromBite: THREE.Matrix4;
  biteFromWorld: THREE.Matrix4;
  /** "interface" = fitted to the opposing occlusal surfaces; "pca" = arch shape fallback. */
  method: "interface" | "pca";
  interfacePoints: number;
  /** True when the anterior direction was resolved from the arch shape. */
  anteriorResolved: boolean;
}

const INTERFACE_MAX_MM = 1.5;
const MIN_INTERFACE_POINTS = 150;

function strideSample(
  geometry: THREE.BufferGeometry,
  budget: number
): { xyz: Float32Array; count: number } {
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  const stride = Math.max(1, Math.ceil(pos.count / budget));
  const count = Math.ceil(pos.count / stride);
  const xyz = new Float32Array(count * 3);
  let k = 0;
  for (let i = 0; i < pos.count && k < count; i += stride, k++) {
    xyz[k * 3] = pos.getX(i);
    xyz[k * 3 + 1] = pos.getY(i);
    xyz[k * 3 + 2] = pos.getZ(i);
  }
  return { xyz, count: k };
}

/**
 * Bite frame: +Z points from the lower toward the upper arch (normal to the occlusal
 * plane), +Y points anterior, +X = Y × Z (patient's right when facing the patient), and
 * the origin sits at the centre of the occlusal contact zone. Jaw rotations then pivot
 * about the arch instead of an arbitrary scanner origin, and "close" means −Z for the upper.
 */
export function computeBiteFrame(
  upper: THREE.BufferGeometry,
  lower: THREE.BufferGeometry,
  lowerBvh: MeshBVH
): BiteFrame {
  const up = strideSample(upper, 20_000);
  const lo = strideSample(lower, 20_000);
  const upPca = principalAxes(up.xyz, up.count);
  const loPca = principalAxes(lo.xyz, lo.count);

  const separation = new THREE.Vector3().subVectors(upPca.mean, loPca.mean);
  const guide =
    separation.lengthSq() > 1e-8 ? separation.clone().normalize() : new THREE.Vector3(0, 0, 1);

  const combined = new Float32Array((up.count + lo.count) * 3);
  combined.set(up.xyz.subarray(0, up.count * 3), 0);
  combined.set(lo.xyz.subarray(0, lo.count * 3), up.count * 3);
  const combinedPca = principalAxes(combined, up.count + lo.count);
  let coarseNormal = combinedPca.vectors[0];
  let bestAlign = -1;
  for (const v of combinedPca.vectors) {
    const align = Math.abs(v.dot(guide));
    if (align > bestAlign) {
      bestAlign = align;
      coarseNormal = v;
    }
  }
  coarseNormal = coarseNormal.clone();
  if (coarseNormal.dot(guide) < 0) coarseNormal.negate();

  const hit = { point: new THREE.Vector3(), distance: 0, faceIndex: 0 };
  const p = new THREE.Vector3();
  const interface_: number[] = [];
  for (let i = 0; i < up.count; i++) {
    p.set(up.xyz[i * 3], up.xyz[i * 3 + 1], up.xyz[i * 3 + 2]);
    if (!lowerBvh.closestPointToPoint(p, hit, 0, INTERFACE_MAX_MM)) continue;
    interface_.push(
      (p.x + hit.point.x) * 0.5,
      (p.y + hit.point.y) * 0.5,
      (p.z + hit.point.z) * 0.5
    );
  }
  const interfaceCount = interface_.length / 3;

  let normal = coarseNormal;
  let origin = new THREE.Vector3().addVectors(upPca.mean, loPca.mean).multiplyScalar(0.5);
  let method: BiteFrame["method"] = "pca";
  if (interfaceCount >= MIN_INTERFACE_POINTS) {
    const ip = principalAxes(interface_, interfaceCount);
    const planar = ip.values[2] < 0.5 * ip.values[1];
    const fitted = ip.vectors[2].clone();
    if (fitted.dot(guide) < 0) fitted.negate();
    if (planar && fitted.dot(coarseNormal) > Math.cos(THREE.MathUtils.degToRad(35))) {
      normal = fitted;
      method = "interface";
    }
    origin = ip.mean.clone();
  }

  // In-plane axes from the upper arch's footprint on the occlusal plane.
  const projected = new Float32Array(up.count * 3);
  const d = new THREE.Vector3();
  for (let i = 0; i < up.count; i++) {
    d.set(up.xyz[i * 3], up.xyz[i * 3 + 1], up.xyz[i * 3 + 2]).sub(origin);
    d.addScaledVector(normal, -d.dot(normal));
    projected[i * 3] = d.x;
    projected[i * 3 + 1] = d.y;
    projected[i * 3 + 2] = d.z;
  }
  const footprint = principalAxes(projected, up.count);
  let xAxis = footprint.vectors[0].clone();
  xAxis.addScaledVector(normal, -xAxis.dot(normal)).normalize();
  if (xAxis.lengthSq() < 0.5) {
    xAxis = new THREE.Vector3(1, 0, 0).addScaledVector(normal, -normal.x).normalize();
  }
  let yAxis = new THREE.Vector3().crossVectors(normal, xAxis).normalize();

  // Posterior end of a full arch is wider (molars); make +Y anterior.
  let anteriorResolved = false;
  let minY = Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < up.count; i++) {
    const y = projected[i * 3] * yAxis.x + projected[i * 3 + 1] * yAxis.y + projected[i * 3 + 2] * yAxis.z;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const depth = maxY - minY;
  if (depth > 1e-6) {
    let minXLow = Infinity;
    let maxXLow = -Infinity;
    let minXHigh = Infinity;
    let maxXHigh = -Infinity;
    for (let i = 0; i < up.count; i++) {
      const px = projected[i * 3];
      const py = projected[i * 3 + 1];
      const pz = projected[i * 3 + 2];
      const y = px * yAxis.x + py * yAxis.y + pz * yAxis.z;
      const x = px * xAxis.x + py * xAxis.y + pz * xAxis.z;
      if (y < minY + depth * 0.2) {
        minXLow = Math.min(minXLow, x);
        maxXLow = Math.max(maxXLow, x);
      } else if (y > maxY - depth * 0.2) {
        minXHigh = Math.min(minXHigh, x);
        maxXHigh = Math.max(maxXHigh, x);
      }
    }
    const widthLow = maxXLow - minXLow;
    const widthHigh = maxXHigh - minXHigh;
    if (Number.isFinite(widthLow) && Number.isFinite(widthHigh)) {
      const ratio = Math.max(widthLow, widthHigh) / Math.max(1e-6, Math.min(widthLow, widthHigh));
      anteriorResolved = ratio > 1.25;
      if (widthHigh > widthLow) yAxis.negate();
    }
  }
  xAxis = new THREE.Vector3().crossVectors(yAxis, normal).normalize();

  const worldFromBite = new THREE.Matrix4().makeBasis(xAxis, yAxis, normal);
  worldFromBite.setPosition(origin);
  const biteFromWorld = worldFromBite.clone().invert();

  return {
    worldFromBite,
    biteFromWorld,
    method,
    interfacePoints: interfaceCount,
    anteriorResolved,
  };
}
